// Safe report generator — uses DOMParser + Blob instead of document.write

function escapeHtml(str) {
  const div = document.createElement('div');
  div.textContent = String(str ?? '');
  return div.innerHTML;
}

export function generateReport({ contact, recipes, ingredients, breakEvenData, pnlData, recommendations }) {
  const e = escapeHtml;
  const avgFoodCost = recipes.length > 0
    ? (recipes.reduce((s, r) => s + (r.foodCostPercent || 0), 0) / recipes.length).toFixed(1)
    : '0';
  const avgPrimeCost = recipes.length > 0
    ? (recipes.reduce((s, r) => s + (r.primeCostPercent || 0), 0) / recipes.length).toFixed(1)
    : '0';

  const totalFixed = ['rent', 'utilities', 'insurance', 'software', 'payroll', 'managerSalary', 'loanPayments', 'otherFixed']
    .reduce((s, k) => s + (Number(breakEvenData[k]) || 0), 0);
  const margin = (Number(breakEvenData.avgContributionMargin) || 1) / 100;
  const avgOrder = Number(breakEvenData.avgOrderValue) || 1;
  const beRevenue = margin > 0 ? totalFixed / margin : 0;
  const beDaily = beRevenue / 30;
  const beOrders = Math.ceil(beDaily / avgOrder);

  const revenue = Number(pnlData.revenue) || 0;
  const totalExpenses = ['cogs', 'labor', 'rent', 'utilities', 'marketing', 'software', 'insurance']
    .reduce((s, k) => s + (Number(pnlData[k]) || 0), 0);
  const netProfit = revenue - totalExpenses;

  const date = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  const recipeRows = recipes.map(r => `
    <tr>
      <td>${e(r.name)}</td><td>${e(r.category)}</td>
      <td style="text-align:right">$${(r.menuPrice || 0).toFixed(2)}</td>
      <td style="text-align:right">$${(r.totalCost || 0).toFixed(2)}</td>
      <td style="text-align:right" class="${(r.grossProfit || 0) >= 0 ? 'profit' : 'loss'}">$${(r.grossProfit || 0).toFixed(2)}</td>
      <td style="text-align:right">${(r.foodCostPercent || 0).toFixed(1)}%</td>
      <td style="text-align:right">${(r.grossMargin || 0).toFixed(1)}%</td>
    </tr>`).join('');

  const tipRows = (recommendations || []).map(tip => `
    <div class="tip tip-${e(tip.type)}">
      <h4>${e(tip.title)}</h4>
      ${tip.why ? `<p class="why">${e(tip.why)}</p>` : ''}
      <p>${e(tip.detail)}</p>
      <ul>${tip.actions.map(a => `<li>${e(a)}</li>`).join('')}</ul>
      ${tip.source ? `<p class="source">${e(tip.source)}</p>` : ''}
    </div>`).join('');

  const resources = [
    { title: 'Food Cost Formula Guide', url: 'https://pos.toasttab.com/blog/on-the-line/food-cost-percentage', source: 'Toast' },
    { title: 'Prime Cost Explained', url: 'https://www.restaurantowner.com/public/Prime-Cost-The-Most-Important-Number.cfm', source: 'RestaurantOwner.com' },
    { title: 'Menu Engineering 101', url: 'https://pos.toasttab.com/blog/on-the-line/menu-engineering', source: 'Toast' },
    { title: 'Restaurant P&L Guide', url: 'https://www.restaurant365.com/blog/average-restaurant-profit-margin/', source: 'Restaurant365' },
    { title: 'Break-Even Calculator Guide', url: 'https://www.sba.gov/business-guide/plan-your-business/calculate-your-startup-costs', source: 'SBA.gov' },
    { title: 'Portion Control Best Practices', url: 'https://www.webstaurantstore.com/article/portion-control-guide.html', source: 'WebstaurantStore' },
  ];

  const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Outfit:wght@400;600;700&display=swap');
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Outfit', sans-serif; padding: 40px; color: #2A2A2A; background: #fff; font-size: 13px; line-height: 1.5; }
  .header { display: flex; align-items: center; gap: 12px; margin-bottom: 8px; padding-bottom: 16px; border-bottom: 3px solid #E23D28; }
  .header-dot { width: 40px; height: 40px; background: #E23D28; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #fff; font-size: 1.2rem; font-family: 'Bebas Neue', cursive; }
  .header-text h1 { font-family: 'Bebas Neue', cursive; font-size: 1.6rem; color: #E23D28; }
  .header-text p { font-size: 11px; color: #888; }
  .contact-bar { background: #FFF8E7; border: 1px solid #F0E6D2; border-radius: 8px; padding: 12px 16px; margin: 16px 0; display: flex; gap: 24px; flex-wrap: wrap; }
  .contact-bar span { font-size: 12px; } .contact-bar strong { color: #2A2A2A; }
  h2 { font-family: 'Bebas Neue', cursive; font-size: 1.3rem; margin: 24px 0 8px; color: #2A2A2A; }
  .stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin: 12px 0; }
  .stat { text-align: center; border: 1px solid #eee; border-radius: 8px; padding: 10px; }
  .stat .label { font-size: 10px; color: #888; text-transform: uppercase; font-family: 'Bebas Neue', cursive; }
  .stat .value { font-size: 1.5rem; font-weight: 700; font-family: 'Bebas Neue', cursive; }
  .green { color: #2E7D32; } .red { color: #E23D28; }
  table { width: 100%; border-collapse: collapse; margin: 8px 0 16px; }
  th { background: #2A2A2A; color: #fff; padding: 6px 10px; text-align: left; font-family: 'Bebas Neue', cursive; font-size: 11px; }
  td { padding: 6px 10px; border-bottom: 1px solid #eee; font-size: 12px; }
  .profit { color: #2E7D32; font-weight: 700; } .loss { color: #E23D28; font-weight: 700; }
  .tip { border-left: 3px solid #ddd; padding: 10px 14px; margin: 10px 0; border-radius: 0 6px 6px 0; background: #fafafa; }
  .tip-critical { border-color: #E23D28; background: #FEF2F2; }
  .tip-warning { border-color: #FFC107; background: #FFFBEB; }
  .tip-success { border-color: #2E7D32; background: #F0FDF4; }
  .tip-bestpractice { border-color: #FFC107; background: #FFFBEB; }
  .tip h4 { font-family: 'Bebas Neue', cursive; font-size: 0.95rem; margin-bottom: 4px; }
  .tip .why { font-style: italic; color: #555; font-size: 11px; margin: 4px 0 6px; padding: 6px; background: rgba(0,0,0,0.03); border-radius: 4px; }
  .tip ul { margin: 6px 0 0 16px; } .tip li { margin: 3px 0; }
  .tip .source { font-size: 10px; color: #999; margin-top: 6px; }
  .resources { display: grid; grid-template-columns: repeat(2, 1fr); gap: 6px; margin: 8px 0; }
  .resources a { font-size: 11px; color: #E23D28; text-decoration: none; padding: 6px 8px; border: 1px solid #eee; border-radius: 6px; display: block; }
  .resources .src { font-size: 10px; color: #aaa; }
  .footer { margin-top: 30px; padding-top: 12px; border-top: 2px solid #eee; text-align: center; color: #999; font-size: 10px; }
  @media print { body { padding: 20px; } .tip { break-inside: avoid; } }`;

  const bodyContent = `
<div class="header">
  <div class="header-dot">P</div>
  <div class="header-text">
    <h1>Mikey Mozzarella's Pizza-lator</h1>
    <p>Restaurant Profitability Report &mdash; ${e(date)}</p>
  </div>
</div>
<div class="contact-bar">
  <span><strong>Prepared for:</strong> ${e(contact.name)}</span>
  ${contact.restaurantName ? `<span><strong>Restaurant:</strong> ${e(contact.restaurantName)}</span>` : ''}
  <span><strong>Email:</strong> ${e(contact.email)}</span>
  <span><strong>Phone:</strong> ${e(contact.phone)}</span>
</div>
<h2>Your Numbers at a Glance</h2>
<div class="stats">
  <div class="stat"><div class="label">Ingredients</div><div class="value">${ingredients.length}</div></div>
  <div class="stat"><div class="label">Recipes</div><div class="value">${recipes.length}</div></div>
  <div class="stat"><div class="label">Avg Food Cost</div><div class="value ${Number(avgFoodCost) > 30 ? 'red' : 'green'}">${avgFoodCost}%</div></div>
  <div class="stat"><div class="label">Break-Even</div><div class="value red">${beOrders}/day</div></div>
</div>
<div class="stats">
  <div class="stat"><div class="label">Avg Prime Cost</div><div class="value ${Number(avgPrimeCost) > 60 ? 'red' : 'green'}">${avgPrimeCost}%</div></div>
  <div class="stat"><div class="label">Monthly Fixed</div><div class="value">$${totalFixed.toLocaleString()}</div></div>
  <div class="stat"><div class="label">Revenue</div><div class="value green">$${revenue.toLocaleString()}</div></div>
  <div class="stat"><div class="label">Net Profit</div><div class="value ${netProfit >= 0 ? 'green' : 'red'}">$${netProfit.toLocaleString()}</div></div>
</div>
${recipes.length > 0 ? `<h2>Recipe Cost Analysis</h2><table>
  <tr><th>Recipe</th><th>Category</th><th style="text-align:right">Price</th><th style="text-align:right">Cost</th><th style="text-align:right">Profit</th><th style="text-align:right">Food Cost %</th><th style="text-align:right">Margin %</th></tr>
  ${recipeRows}</table>` : ''}
<h2>Mikey's Analysis &amp; Recommendations</h2>
${tipRows}
<h2>Recommended Resources</h2>
<div class="resources">
  ${resources.map(r => `<a href="${e(r.url)}" target="_blank">${e(r.title)} <span class="src">(${e(r.source)})</span></a>`).join('')}
</div>
<div class="footer">
  Mikey Mozzarella's Pizza-lator &mdash; Know Your Dough. &mdash; Generated ${e(date)}<br/>
  This report is for informational purposes. Consult a financial advisor for specific business decisions.
</div>`;

  // Build HTML as Blob, open via object URL — no document.write
  const fullHtml = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Pizza-lator Report - ${e(contact.restaurantName || contact.name)}</title><style>${styles}</style></head><body>${bodyContent}</body></html>`;
  const blob = new Blob([fullHtml], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const win = window.open(url, '_blank', 'width=900,height=700');
  if (win) {
    win.addEventListener('afterprint', () => URL.revokeObjectURL(url));
    setTimeout(() => { win.print(); URL.revokeObjectURL(url); }, 600);
  } else {
    URL.revokeObjectURL(url);
  }
}
